import DropdownContext from '../dropdown/dropdown-context';
import {ChangeEvent, FocusEvent, MutableRefObject, useEffect, useRef, useState} from 'react';
import {join} from '../../utils';
import {Popup} from '../dropdown/popup/popup';
import {Option} from '../dropdown/option';
import {Input} from '../input/input';
import {Selectors} from '../controls.selectors';
import styles from './styles.module.less';

export interface AutocompleteOption {
	label: string;
	value: string | number;
}

interface InputAutocompleteProps<OptionType extends AutocompleteOption> {
	options: OptionType[];
	value: string | number;
	inputType?: 'number' | 'text';
	inputClassName?: string;
	optionClassName?: string,
	placeholder?: string;
	onChange?: (option: OptionType | null) => void;
	onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	disabled?: boolean;
	fieldRef?: ((el: HTMLInputElement) => void) | MutableRefObject<HTMLInputElement>;
}

export function InputAutocomplete<Option extends AutocompleteOption>(props: InputAutocompleteProps<Option>) {
	const{
		options,
		value: selectedValue,
		inputType = 'number',
		inputClassName, optionClassName,
		placeholder,
		disabled,
		onChange, onFocus,
		onBlur,
		fieldRef,
	}= props;
	const inputRef = useRef(null);
	const [isToggled, setToggled] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
	const [value, setValue] = useState([]);
	const [isFocused, setFocused] = useState(false);

	const blurTimeout = useRef<ReturnType<typeof setTimeout>>(null);

	const handleOptionSelect = (selectedOption: Option['value']) => {
		clearTimeout(blurTimeout.current || 0);
		const option = filteredOptions.find(item => item.value === selectedOption);
		// @ts-ignore
		setValue([option]);
		onChange?.(option as any);
		setInputValue(option?.label || '');
		setToggled(false);
		setFilteredOptions(options);
	};

	const handleDropdownBlur = () => {
		if (!isFocused) {
			setToggled(false);
		}
	};

	const filterOptions = (searchText: string) => {
		return !searchText ? options : options.filter(item => item.label.includes(searchText));
	};

	const handleInputChange = ({target: {value}}: ChangeEvent<HTMLInputElement>) => {
		const newFilteredOptions = filterOptions(value);
		setValue([]);
		onChange?.(null);
		setInputValue(value);
		setFilteredOptions(newFilteredOptions);
		setToggled(!!newFilteredOptions.length);
	};

	const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
		onBlur?.(e);
		setFocused(false);
	};

	const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
		onFocus?.(e);
		setFocused(true);
		if (filteredOptions.length) {
			setToggled(true);
		}
	};

	useEffect(() => {
		if (!isFocused && inputValue) {
			blurTimeout.current = setTimeout(() => {
				if (!value.length) {
					const existingOption = options.find(item => item.label === inputValue);
					if (!existingOption) {
						setInputValue('');
						onChange?.(null);
						setFilteredOptions(options);
					} else {
						// @ts-ignore
						setValue([existingOption]);
						onChange?.(existingOption);
					}
				}
			}, 500);
		}

		// @ts-ignore
		return () => clearTimeout(blurTimeout.current);
	}, [isFocused, value, inputValue]);

	useEffect(() => {
		const selectedOption = options.find(item => item.value === selectedValue);
		if (selectedOption) {
			// @ts-ignore
			setValue([selectedOption]);
			setInputValue(selectedOption.label);
			setFilteredOptions(options);
		}

	}, [selectedValue]);

	return (
		<div data-cy={Selectors.InputAutocomplete}>
			<Input type={inputType}
						 ref={(r) => {
							 inputRef.current = r;
							 if (fieldRef) {
								 if (typeof fieldRef === 'function') {
									 fieldRef(r);
								 } else {
									 fieldRef.current = r;
								 }
							 }
						 }
						 }
						 className={join(inputClassName, styles.input)}
						 placeholder={placeholder}
						 disabled={disabled}
						 value={inputValue}
						 onFocus={handleInputFocus}
						 onBlur={handleInputBlur}
						 onChange={handleInputChange}/>
			<DropdownContext.Provider value={{
				dropdownRef: inputRef.current,
				currentValue: value,
				multiselect: false,
				toggled: isToggled,
				onOptionClick: handleOptionSelect,
			}}>
				<Popup
					toggled={isToggled}
					key={filteredOptions.length}
					targetRef={inputRef}
					onLooseFocus={handleDropdownBlur}
				>
					{filteredOptions.map(({value, label}) => (
						<Option
							key={value}
							value={value}
							className={join(optionClassName && optionClassName)}
						>
							{label}
						</Option>
					))}
				</Popup>
			</DropdownContext.Provider>
		</div>
	);
}
